import { useEffect, useState, useCallback } from "react";
import api from "../api/api";

function VendorDashboard() {
  const [deliveries,  setDeliveries]  = useState([]);
  const [tab,         setTab]         = useState("PENDING");
  const [search,      setSearch]      = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [sortArea, setSortArea] = useState("");
  const userName = localStorage.getItem("userName") || "Vendor";

  /* ── Fetch deliveries ─────────────────────────── */
  const fetchDeliveries = useCallback(async () => {
    try {
      const res = await api.get(`/vendor/deliveries?date=${selectedDate}`);
      setDeliveries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDeliveries([]);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDeliveries();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchDeliveries();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  /* ── Mark delivered ───────────────────────────── */
  const markDelivered = async (subscriptionId) => {
    try {
      await api.post(
        `/vendor/deliver?subscriptionId=${subscriptionId}&status=DELIVERED`
      );
      setDeliveries(prev =>
        prev.map(d =>
          d.subscriptionId === subscriptionId ? { ...d, status: "DELIVERED" } : d
        )
      );
    } catch {
      alert("Failed to update delivery status");
    }
  };

  /* ── Derived state ────────────────────────────── */
  const pending   = deliveries.filter(d => d.status === "PENDING");
  const delivered = deliveries.filter(d => d.status === "DELIVERED");
  const total     = deliveries.length;
  const progress  = total > 0 ? Math.round((delivered.length / total) * 100) : 0;
  const uniqueAreas = [...new Set(deliveries.map(d => d.areaName).filter(Boolean))];

  let filtered = (tab === "PENDING" ? pending : delivered)
    .filter(d => d.userName?.toLowerCase().includes(search.toLowerCase()))
    .filter(d => !sortArea || d.areaName === sortArea);

  return (
    <div className="app-shell">
      {/* ── SIDEBAR ──────────────────────────────── */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-mark">📦</div>
          <div className="logo-name">PressManager</div>
          <div className="logo-tagline">Vendor Portal</div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-group-label">Deliveries</div>
          <div className="nav-item active">
            <span className="nav-icon">🚴</span>
            My Deliveries
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{userName[0]?.toUpperCase()}</div>
            <div className="user-info">
              <div className="user-name">{userName}</div>
              <div className="user-role">Delivery Vendor</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN ─────────────────────────────────── */}
      <main className="main-content">
        <div className="topbar">
          <div className="topbar-title">My Deliveries</div>
          <div className="topbar-right">
            <span style={{ fontSize: 11, color: "var(--sage)", fontWeight: 600 }}>
              🟢 Live Updates On
            </span>
            <span className="role-pill vendor">Vendor</span>
          </div>
        </div>

        <div className="page">
          {/* ── Filters ────────────────────────── */}
          <div className="flex-center gap-12 mb-24" style={{ flexWrap: "wrap" }}>
            <div className="search-bar">
              <span className="search-icon">🔍</span>
              <input className="form-input" placeholder="Search customer…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>

            <div className="form-group" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: "nowrap" }}>Date:</label>
              <input className="form-input" type="date" value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                style={{ width: "auto" }} />
            </div>

            {uniqueAreas.length > 0 && (
              <select className="form-select" style={{ width: "auto" }}
                onChange={e => setSortArea(e.target.value)}>
                <option value="">All Areas</option>
                {uniqueAreas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </div>

          {/* ── Summary ────────────────────────── */}
          <div className="vendor-summary-grid">
            <div className="vsummary-card">
              <div className="vsummary-num">{total}</div>
              <div className="vsummary-lbl">Total Today</div>
            </div>
            <div className="vsummary-card">
              <div className="vsummary-num" style={{ color: "var(--sage)" }}>{delivered.length}</div>
              <div className="vsummary-lbl">Delivered</div>
            </div>
            <div className="vsummary-card">
              <div className="vsummary-num" style={{ color: "var(--gold)" }}>{pending.length}</div>
              <div className="vsummary-lbl">Pending</div>
            </div>
          </div>

          {/* ── Progress ───────────────────────── */}
          <div className="card mb-24">
            <div className="card-body">
              <div className="flex-between mb-16">
                <span className="fw-600">Today's Progress</span>
                <span className="fw-700 text-success">{progress}% Complete</span>
              </div>
              <div className="progress-bar" style={{ height: 12 }}>
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>

          {/* ── Tabs ───────────────────────────── */}
          <div className="tabs">
            <button className={`tab-btn ${tab === "PENDING" ? "active" : ""}`}
              onClick={() => setTab("PENDING")}>
              ⏳ Pending ({pending.length})
            </button>
            <button className={`tab-btn ${tab === "DELIVERED" ? "active" : ""}`}
              onClick={() => setTab("DELIVERED")}>
              ✅ Delivered ({delivered.length})
            </button>
          </div>

          {/* ── Delivery list ──────────────────── */}
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{tab === "PENDING" ? "🎉" : "📭"}</div>
              <div className="empty-title">
                {tab === "PENDING" ? "All deliveries done!" : "No deliveries yet"}
              </div>
              <div className="empty-sub">
                {tab === "PENDING" ? "Great work today!" : "Delivered items will appear here"}
              </div>
            </div>
          ) : (
            filtered.map(d => (
              <div
                key={d.subscriptionId}
                className={`delivery-card ${d.status === "DELIVERED" ? "delivered" : "pending"}`}
              >
                <div className="delivery-head">
                  <div>
                    <div className="delivery-paper">📰 {d.newspaperName}</div>
                    <div className="delivery-customer">{d.userName}</div>
                  </div>
                  <span className={`badge ${d.status === "DELIVERED" ? "badge-success" : "badge-warning"}`}>
                    {d.status}
                  </span>
                </div>

                <div className="delivery-meta">
                  <span className="delivery-meta-item">📱 {d.phone}</span>
                  <span className="delivery-meta-item">🏠 {d.address || "No address"}</span>
                  <span className="delivery-meta-item">📍 {d.areaName}</span>
                </div>

                {d.status === "PENDING" && (
                  <button
                    className="btn btn-success btn-sm mt-16"
                    onClick={() => markDelivered(d.subscriptionId)}
                  >
                    ✓ Mark as Delivered
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

export default VendorDashboard;
